import { useEffect, useMemo, useState } from "react";

import { Form, FormikProvider, FormikValues, useFormik } from "formik";
import { App, Spin, Table } from "antd";
import { useAtom, useAtomValue } from "jotai";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { mixed, object, string } from "yup";

import { ReactComponent as Trash } from "../../assets/trash.svg";
import { ReactComponent as Edit } from "../../assets/pencil-paper.svg";
import { Input, Select } from "../../custom";
import Upload from "../../custom/upload/upload";
import styles from "./styles.module.scss";
import api from "../../utils/api";
import { pageIndexAtom, userAtom } from "../../utils/store";
import OnboardingButtonGroup from "./onboardingButtonGroup";
import { getGrades, getSubjects } from "../../utils/request";
import {
  isPdfOrImageFile,
  PDF_IMAGE_ACCEPT,
  PDF_IMAGE_FORMAT_LABELS,
} from "../../utils/fileValidation";

interface Subject {
  subject: string;
  grade: string;
}

interface QualificationResponse extends Response {
  data: Qualification[];
}

// Exact names only - substring matching would also catch "Further
// Mathematics" (contains "mathematic") and "Literature-in-English"
// (contains "english"), wrongly treating them as compulsory.
const COMPULSORY_SUBJECT_NAMES = ["english", "english language", "mathematics"];

const isCompulsorySubject = (subjectName?: string) => {
  const normalizedName = subjectName?.trim().toLowerCase() ?? "";
  return COMPULSORY_SUBJECT_NAMES.includes(normalizedName);
};

// The auto-prefilled Mathematics/English rows (blank grade) don't count as
// "the applicant has entered subject data" on their own, otherwise the
// qualification form would always think a subject was added and skip the
// required qualificationType/examNumber/certificate fields.
const hasMeaningfulSubjects = (subjectList: Subject[]) =>
  subjectList.some((item) => !isCompulsorySubject(item.subject) || !!item.grade);

const Qualification = () => {
  const { notification } = App.useApp();

  const user = useAtomValue(userAtom);
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [subjectToEdit, setSubjectToEdit] = useState<Subject>({
    subject: "",
    grade: "",
  });
  const [isEditingQualification, setIsEditingQualification] = useState(false);
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [currentId, setCurrentId] = useState(0);
  const [shouldContinue, setShouldContinue] = useState(false);

  const validationRules = object().shape({
    qualificationType: string().required("Qualification type is required"),
    examNumber: string().required("Exam number is required"),
    subject: string().when([], {
      is: () => isEditingQualification || hasMeaningfulSubjects(subjects),
      then: (passSchema) => passSchema,
      otherwise: (passSchema) => passSchema.required("Subject is required"),
    }),
    grade: string().when([], {
      is: () => isEditingQualification || hasMeaningfulSubjects(subjects),
      then: (passSchema) => passSchema,
      otherwise: (passSchema) => passSchema.required("Grade is required"),
    }),
    certificate: mixed().when([], {
      is: () => isEditingQualification || hasMeaningfulSubjects(subjects),
      then: (passSchema) => passSchema,
      otherwise: (passSchema) =>
        passSchema
          .required("Certificate is required")
          .test(
            "fileFormat",
            "*Only image or pdf files are allowed",
            (value) => isPdfOrImageFile(value as File | null),
          )
          .test("fileSize", "File size must be less than 3MB", (value) => {
            if (value) {
              return (value as File)?.size <= 3145728;
            }
            return true;
          }),
    }),
  });

  const queries = useQueries({
    queries: [
      { queryKey: ["get-all-subject"], queryFn: getSubjects },
      { queryKey: ["get-all-grades"], queryFn: getGrades },
    ],
  });

  const [subjectQuery, gradeQuery] = queries;

  const {
    data: subjectData,
    isLoading: isSubjectLoading,
    isError: isSubjectError,
    error: subjectError,
  } = subjectQuery;

  const {
    data: gradeData,
    isLoading: isGradeLoading,
    isError: isGradeError,
    error: gradeError,
  } = gradeQuery;

  const subject = useMemo(() => subjectData?.data ?? [], [subjectData?.data]);
  const grades = gradeData?.data ?? [];

  const compulsorySubjects = useMemo(
    () => subject.filter((item) => isCompulsorySubject(item?.subject)),
    [subject],
  );

  // Mathematics and English must always be present in the subject list;
  // grade is left blank/optional until the applicant fills it in.
  useEffect(() => {
    if (!compulsorySubjects.length) return;

    const missingSubjects = compulsorySubjects.filter(
      (item) => !subjects.some((s) => s.subject === item.subject),
    );

    if (!missingSubjects.length) return;

    setSubjects((prev) => [
      ...missingSubjects.map((item) => ({ subject: item.subject, grade: "" })),
      ...prev,
    ]);
  }, [compulsorySubjects, subjects]);

  const createQualificationMutation = useMutation({
    mutationFn: async (payload: FormData) => {
      return (await api.post(
        "/StudentInfo/StudentInfo/CreateUpdateQualification",
        payload,
      )) as Response;
    },
  });

  const deleteQualificationMutation = useMutation({
    mutationFn: async (id: string | number) => {
      return (await api.delete(
        `/StudentInfo/StudentInfo/DeleteQualification?Id=${id}`,
      )) as Response;
    },
  });

  const qualificationQuery = useQuery({
    queryKey: ["get-all-qualifications"],
    queryFn: async () => {
      return (await api.get(`/StudentInfo/StudentInfo/GetAllQualification`))
        ?.data as QualificationResponse;
    },
  });

  const qualification = qualificationQuery.data?.data as Qualification[];

  const handleCreateorUpdateQualification = async (
    values: FormikValues,
    resetForm: () => void,
  ) => {
    const payload = new FormData();
    try {
      payload.append("Id", currentId ? `${currentId}` : "0");
      payload.append("ApplicantId", user?.applicantId ?? "");
      payload.append("QualificationType", values.qualificationType);
      payload.append("ExamNumber", values.examNumber);
      if (values.certificate) {
        payload.append("Certificate", values.certificate);
      }
      payload.append("CertificateiUrl", "");
      values.subjects?.forEach((subject: Subject, index: number) => {
        payload.append(`Subjects[${index}].subject`, subject.subject);
        payload.append(`Subjects[${index}].grade`, subject.grade);
        payload.append(`Subjects[${index}].activeStatus`, "true");
        payload.append(`Subjects[${index}].isDeleted`, "false");
      });
      payload.append("isDeleted", "false");
      payload.append("IsSubmmited", "true");
      await createQualificationMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          setSubjects([]);
          resetForm();
          setIsEditingQualification(false);
          setCurrentId(0);
          qualificationQuery.refetch();
          if (shouldContinue) {
            setShouldContinue(false);
            setPageIndex(pageIndex + 1);
          }
        },
      });
    } catch (error: any) {
      console.log(error);
      setShouldContinue(false);

      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const handleDeleteQualification = async (id: string | number) => {
    try {
      await deleteQualificationMutation.mutateAsync(id, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          qualificationQuery.refetch();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.message || error?.response?.data?.message,
      });
    }
  };

  const formik = useFormik<FormikValues>({
    initialValues: {
      qualificationType: "",
      examNumber: "",
      subjects: [],
      certificate: "",
      subject: "",
      grade: "",
    },
    onSubmit: async (values, { resetForm }) => {
      handleCreateorUpdateQualification(values, resetForm);
    },
    validationSchema: validationRules,
  });

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      render: (_: any, _i: any, index: number) => index + 1,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Grade",
      dataIndex: "grade",
      key: "grade",
    },
    {
      title: "",
      dataIndex: "grade",
      key: "grade",
      render: (_: string, record: Subject) => (
        <div className="small-gap">
          <button
            type="button"
            onClick={() => {
              setIsEditingSubject(true);
              setSubjectToEdit(record);
              formik.setFieldValue("subject", record?.subject);
              formik.setFieldValue("grade", record?.grade);
            }}
            className={styles.transparentBtn}
          >
            <Edit />
          </button>
          {!isCompulsorySubject(record?.subject) && (
            <button
              type="button"
              onClick={() =>
                setSubjects(
                  subjects?.filter(
                    (x) =>
                      x.grade !== record?.grade ||
                      x.subject !== record?.subject,
                  ),
                )
              }
              className={styles.transparentBtn}
            >
              <Trash />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleAddSubject = () => {
    const isCompulsory = isCompulsorySubject(formik.values.subject);
    const hasValue =
      !!formik.values.subject && (!!formik.values.grade || isCompulsory);
    const alreadyExists = subjects?.some(
      (x) => x.subject === formik.values.subject,
    );
    if (hasValue) {
      if (alreadyExists && !isEditingSubject) {
        notification.error({
          message: "Subject already exists",
        });
      } else {
        if (isEditingSubject) {
          let index = subjects?.findIndex(
            (x) =>
              x.subject === subjectToEdit?.subject &&
              x.grade === subjectToEdit?.grade,
          );
          if (index !== -1) {
            subjects[index].subject = formik.values.subject;
            subjects[index].grade = formik.values.grade;
          }
          setIsEditingSubject(false);
        } else {
          setSubjects([
            ...subjects,
            {
              subject: formik.values.subject,
              grade: formik.values.grade,
            },
          ]);
          formik.setFieldValue("subject", "");
          formik.setFieldValue("grade", "");
        }
      }
    }
  };

  const editQualification = (item: Qualification) => {
    setIsEditingQualification(true);
    formik.setFieldValue("qualificationType", item.qualificationType);
    formik.setFieldValue("examNumber", item.examNumber);
    setSubjects(item.subjects);
    setCurrentId(item.id);
  };

  const getMissingCompulsorySubjects = () =>
    compulsorySubjects
      .filter((item) => {
        const entry = subjects.find((s) => s.subject === item.subject);
        return !entry?.grade;
      })
      .map((item) => item.subject);

  const handleSubmitForm = async () => {
    const missingGrades = getMissingCompulsorySubjects();
    if (missingGrades.length) {
      notification.error({
        message: "Error",
        description: `Grade has not been added for ${missingGrades.join(" and ")}`,
      });
      return;
    }

    setShouldContinue(false);
    await formik.setFieldValue("subjects", subjects);
    await formik.submitForm();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files;
    if (file) {
      formik.setFieldValue("certificate", file[0]);
    }
  };

  const handlePrevClick = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const handleNextClick = async () => {
    const hasDraftQualification =
      !!formik.values.qualificationType ||
      !!formik.values.examNumber ||
      !!formik.values.certificate ||
      hasMeaningfulSubjects(subjects) ||
      isEditingQualification;

    if (hasDraftQualification) {
      const missingGrades = getMissingCompulsorySubjects();
      if (missingGrades.length) {
        notification.error({
          message: "Error",
          description: `Grade has not been added for ${missingGrades.join(" and ")}`,
        });
        return;
      }

      setShouldContinue(true);
      await formik.setFieldValue("subjects", subjects);
      await formik.submitForm();
      return;
    }

    if (qualification?.length === 0) {
      notification.error({
        message: "Error",
        description: "Please completely fill the form to proceed",
      });
    } else {
      setPageIndex(pageIndex + 1);
    }
  };

  const subjectOptions = subject?.map((item) => {
    if (isSubjectLoading) {
      return <option value="">{<Spin size="small" />}</option>;
    } else if (isSubjectError) {
      return <option value="">{subjectError?.message}</option>;
    } else {
      return (
        <option key={item?.id} value={item?.subject}>
          {item?.subject}
        </option>
      );
    }
  });

  const gradeOptions = grades?.map((item) => {
    if (isGradeLoading) {
      return <option value="">{<Spin size="small" />}</option>;
    } else if (isGradeError) {
      return <option value="">{gradeError?.message}</option>;
    } else {
      return (
        <option key={item?.id} value={item?.grade}>
          {item?.grade}
        </option>
      );
    }
  });

  return (
    <FormikProvider value={formik}>
      <Form>
        <section className={styles.section}>
          <h2>Qualifications</h2>
          <br />
          <p>Input details of your attained qualifications (if any)</p>
        </section>
        <hr />
        <div className={styles.section}>
          {qualification?.length > 0 &&
            qualification
              ?.filter((x) => x.applicantId === user?.applicantId)
              ?.map((x) => (
                <section key={x.examNumber} className="space-between">
                  <div>
                    <h5>{x.qualificationType}</h5>
                    <small>{x.examNumber}</small>
                  </div>
                  <div className="small-gap">
                    <button
                      onClick={() => editQualification(x)}
                      type="button"
                      className={styles.transparentBtn}
                    >
                      <Edit />
                    </button>
                    <button
                      onClick={() => handleDeleteQualification(x.id)}
                      type="button"
                      className={styles.transparentBtn}
                    >
                      <Trash />
                    </button>
                  </div>
                </section>
              ))}

          <br />

          <div className={styles.formWrapper}>
            <Input
              name="qualificationType"
              label="Qualification Type"
              placeholder="Enter qualification"
            />
            <Input
              name="examNumber"
              label="Exam Number"
              placeholder="Enter exam number"
            />

            <section className={styles.grid}>
              <Select
                name="subject"
                label="Subject"
                placeholder="Select subject"
                options={subjectOptions}
                onChange={formik.handleChange}
              />
              <Select
                name="grade"
                label="Grade"
                placeholder="Select grade"
                options={gradeOptions}
                onChange={formik.handleChange}
              />
            </section>

            {/* <section className={styles.grid}>
              <Input name="subject" label="Subject" placeholder="Subject" />
              <Input name="grade" label="Grade" placeholder="Enter grade" />
            </section> */}
          </div>

          <button
            type="button"
            onClick={handleAddSubject}
            className={styles.addSubject}
          >
            <span>+</span>
            <span>{isEditingSubject ? "Save" : "Add"} Subject</span>
          </button>

          <br />

          {subjects?.length > 0 && (
            <Table
              dataSource={subjects}
              columns={columns}
              pagination={false}
              rowKey={(record) => record?.subject}
            />
          )}

          <br />

          <Upload
            fileName={formik.values.certificate?.name}
            fileSize={formik.values.certificate?.size}
            onChange={handleFileChange}
            name="certificate"
            label="Upload Certificate"
            accept={PDF_IMAGE_ACCEPT}
            allowedFormats={PDF_IMAGE_FORMAT_LABELS}
          />

          <br />

          <button
            onClick={handleSubmitForm}
            type="button"
            className={styles.addQualification}
          >
            <div>
              <span>+</span>
            </div>
            <span>
              {isEditingQualification ? (
                <b>
                  {createQualificationMutation.isPending
                    ? "Saving..."
                    : "Save Changes"}
                </b>
              ) : (
                <b>
                  {createQualificationMutation.isPending
                    ? "Adding Qualification..."
                    : "Add Qualification"}
                </b>
              )}
            </span>
          </button>

          <OnboardingButtonGroup
            onClick={handlePrevClick}
            onNextClick={handleNextClick}
          />
        </div>
      </Form>
    </FormikProvider>
  );
};

export default Qualification;
